import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { cachedGet } from '../utils/apiCache';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import AppLayout from '../components/AppLayout';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { 
  FolderOpen,
  Clock,
  CheckCircle2,
  XCircle,
  Award,
  MoreVertical,
  X,
  Trash2,
  ExternalLink,
  Mail,
  Loader2,
  Send,
  FileText,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  MapPin,
  Zap
} from 'lucide-react';

const SHOW_BURST_AUTO_APPLY = false;
// Statuses where the user still has to submit the application on the employer's site
const PREP_STATUSES = ['interested', 'preparing', 'ready', 'pending'];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


const Applications = () => {
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [contactingId, setContactingId] = useState(null);
  const [contactedIds, setContactedIds] = useState(new Set());
  const [expandedApp, setExpandedApp] = useState(null);
  const [columnPages, setColumnPages] = useState({});
  const [listPage, setListPage] = useState(0);
  const [burstApplying, setBurstApplying] = useState(null);
  const { user, getAuthHeaders } = useAuth();
  const ITEMS_PER_PAGE = 10;

  useEffect(() => {
    fetchApplications();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchApplications = async () => {
    try {
      setLoadError(null);
      const { data } = await cachedGet(`${API}/applications/list`, { headers: getAuthHeaders() }, 30 * 1000, (fresh) => setApplications(fresh || []));
      setApplications(data || []);
    } catch (error) {
      console.error('Failed to fetch applications:', error);
      const code = error?.response?.status;
      const msg = code === 401 ? 'Session expired. Please log in again.' :
                  code === 403 ? 'Access denied.' :
                  code >= 500 ? 'Server error. Try again in a moment.' :
                  'Could not load applications. Check your connection.';
      setLoadError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (appId, newStatus) => {
    try {
      await axios.patch(
        `${API}/applications/${appId}/status?status=${newStatus}`,
        {},
        { headers: getAuthHeaders() }
      );
      setApplications(apps => 
        apps.map(app => app.id === appId ? { ...app, status: newStatus } : app)
      );
      toast.success('Status updated!');
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const deleteApplication = async (appId) => {
    if (!window.confirm('Are you sure you want to delete this application?')) return;
    try {
      await axios.delete(`${API}/applications/${appId}`, { headers: getAuthHeaders() });
      setApplications(apps => apps.filter(app => app.id !== appId));
      toast.success('Application deleted');
    } catch (error) {
      toast.error('Failed to delete application');
    }
  };

  const contactRecruiter = async (appId, company) => {
    setContactingId(appId);
    try {
      const res = await axios.post(`${API}/applications/${appId}/contact-recruiter`, {}, { headers: getAuthHeaders() });
      if (res.data.status === 'sending') {
        toast.success(`🚀 Pitch sent to ${company}!`, { duration: 4000 });
        setContactedIds(prev => new Set([...prev, appId]));
      } else if (res.data.status === 'already_sent') {
        toast.info(`Already contacted ${company}`, { duration: 3000 });
        setContactedIds(prev => new Set([...prev, appId]));
      } else if (res.data.status === 'no_email') {
        toast.error(res.data.message, { duration: 4000 });
      }
    } catch {
      toast.error('Failed to contact recruiter');
    } finally {
      setContactingId(null);
    }
  };

  const filteredApplications = (Array.isArray(applications) ? applications : [])
    .filter(app => app && (statusFilter === 'all' || app.status === statusFilter))
    .sort((a, b) => {
      const priority = { interview: 0, offer: 1, applied: 2, ready: 3, pending: 3, preparing: 4, interested: 5, rejected: 6 };
      return (priority[a.status] ?? 5) - (priority[b.status] ?? 5);
    });

  // Reset pagination when filter changes
  useEffect(() => {
    setColumnPages({});
    setListPage(0);
  }, [statusFilter]);

  const statusConfig = {
    interested: { label: 'Interested', color: 'bg-pink-100 text-pink-800', icon: <Clock className="w-4 h-4" /> },
    preparing: { label: 'Preparing Application', color: 'bg-purple-100 text-purple-800', icon: <Clock className="w-4 h-4" /> },
    ready: { label: 'Ready to Apply', color: 'bg-yellow-100 text-yellow-800', icon: <Clock className="w-4 h-4" /> },
    pending: { label: 'Ready to Apply', color: 'bg-yellow-100 text-yellow-800', icon: <Clock className="w-4 h-4" /> },
    applied: { label: 'Applied', color: 'bg-blue-100 text-blue-800', icon: <CheckCircle2 className="w-4 h-4" /> },
    interview: { label: 'Interview', color: 'bg-teal-100 text-teal-800', icon: <Award className="w-4 h-4" /> },
    offer: { label: 'Offer', color: 'bg-green-100 text-green-800', icon: <Award className="w-4 h-4" /> },
    rejected: { label: 'Rejected', color: 'bg-red-100 text-red-800', icon: <XCircle className="w-4 h-4" /> }
  };

  const sourceColors = {
    linkedin: 'bg-blue-500',
    indeed: 'bg-purple-500',
    glassdoor: 'bg-teal-500'
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true
    });
  };

  // Group by status for Kanban view
  const groupedApplications = {
    pending: filteredApplications.filter(a => PREP_STATUSES.includes(a.status)),
    applied: filteredApplications.filter(a => a.status === 'applied'),
    interview: filteredApplications.filter(a => a.status === 'interview'),
    offer: filteredApplications.filter(a => a.status === 'offer'),
    rejected: filteredApplications.filter(a => a.status === 'rejected')
  };

  const getColumnPage = (status) => columnPages[status] || 0;
  const setColumnPage = (status, page) => setColumnPages(prev => ({ ...prev, [status]: page }));

  const paginateItems = (items, page) => {
    const start = page * ITEMS_PER_PAGE;
    return items.slice(start, start + ITEMS_PER_PAGE);
  };

  const totalListPages = Math.ceil(filteredApplications.length / ITEMS_PER_PAGE);
  const paginatedListItems = paginateItems(filteredApplications, listPage);

  const handleBurstApply = async (targetCount) => {
    const plan = user?.subscription_plan;
    if (plan !== 'power' && plan !== 'unlimited' && !user?.is_founder) {
      toast.error('Burst auto-apply requires the Power or Unlimited plan');
      navigate('/subscription');
      return;
    }
    setBurstApplying(targetCount);
    try {
      const res = await axios.post(
        `${API}/auto-apply/burst`,
        { target_count: targetCount, min_match_score: 50, remote_only: false },
        { headers: getAuthHeaders() }
      );
      toast.success(res.data.message || `Auto-applying to ${targetCount} jobs in the background.`);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to start burst auto-apply';
      toast.error(detail);
      if (detail.toLowerCase().includes('upload a cv')) {
        setTimeout(() => navigate('/cv'), 1200);
      }
    } finally {
      setTimeout(() => setBurstApplying(null), 2000);
    }
  };

  return (
    <AppLayout>
      <div data-testid="applications-page">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div>
              <h1 className="font-heading text-3xl font-bold text-zinc-900 dark:text-white mb-2">Applications</h1>
              <p className="text-zinc-600 dark:text-zinc-400">Track your job application progress</p>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48 h-12 rounded-xl border-zinc-200" data-testid="status-filter">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="pending">Ready to Apply</SelectItem>
                <SelectItem value="preparing">Preparing</SelectItem>
                <SelectItem value="interested">Interested</SelectItem>
                <SelectItem value="applied">Applied</SelectItem>
                <SelectItem value="interview">Interview</SelectItem>
                <SelectItem value="offer">Offer</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Burst Auto-Apply — hidden in v1 (JobSurfAI does not submit applications). Code kept for a future release. */}
          {SHOW_BURST_AUTO_APPLY && ((user?.subscription_plan === 'power' || user?.subscription_plan === 'unlimited' || user?.is_founder) ? (
            <div className="bg-gradient-to-r from-amber-500/10 via-orange-500/10 to-rose-500/10 border border-amber-500/30 rounded-2xl p-4 sm:p-5 mb-6 shadow-soft" data-testid="burst-auto-apply-panel-applications">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-amber-500/20 border border-amber-500/30 flex items-center justify-center text-amber-400 flex-shrink-0">
                    <Zap className="w-5 h-5" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-sm sm:text-base font-bold text-zinc-900 dark:text-white">One-click auto-apply</h3>
                    <p className="text-xs text-zinc-600 dark:text-zinc-400 mt-0.5">Pick a target — we&apos;ll auto-apply to that many top-matched jobs in the background.</p>
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-2 sm:flex sm:flex-wrap sm:justify-end">
                  {[50, 100, 200, 500].map((n) => (
                    <Button
                      key={n}
                      onClick={() => handleBurstApply(n)}
                      disabled={burstApplying !== null}
                      className={`rounded-xl font-semibold ${
                        n === 500
                          ? 'bg-rose-500 hover:bg-rose-600 text-white shadow-lg shadow-rose-500/20'
                          : n === 200
                          ? 'bg-orange-500 hover:bg-orange-600 text-white shadow-lg shadow-orange-500/20'
                          : 'bg-amber-500 hover:bg-amber-600 text-white shadow-lg shadow-amber-500/20'
                      } ${burstApplying === n ? 'opacity-80' : ''}`}
                      data-testid={`burst-apply-${n}-btn-applications`}
                    >
                      {burstApplying === n ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <><Zap className="w-4 h-4 mr-1" />{n}</>
                      )}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-zinc-50 dark:bg-white/[0.02] border border-dashed border-zinc-200 dark:border-white/[0.06] rounded-2xl p-4 sm:p-5 mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="burst-auto-apply-locked-applications">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-500/15 border border-amber-500/25 flex items-center justify-center text-amber-500 flex-shrink-0">
                  <Zap className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-sm sm:text-base font-bold text-zinc-900 dark:text-white">Auto-apply to 50, 100, 200 or 500 jobs in one click</h3>
                  <p className="text-xs text-zinc-600 dark:text-zinc-400 mt-0.5">Available on Power and Unlimited plans.</p>
                </div>
              </div>
              <Button onClick={() => navigate('/subscription')} className="rounded-xl bg-amber-500 hover:bg-amber-600 text-white">
                Unlock Auto-Apply
              </Button>
            </div>
          ))}

          {loading ? (
            <div className="space-y-4" data-testid="applications-skeleton">
              <div className="hidden lg:grid lg:grid-cols-5 gap-4">
                {Array.from({ length: 5 }).map((_, c) => (
                  <div key={c} className="space-y-3">
                    <div className="h-6 bg-zinc-800/40 rounded animate-pulse w-3/4" />
                    {Array.from({ length: 2 }).map((_, r) => (
                      <div key={r} className="h-28 bg-zinc-800/30 rounded-2xl animate-pulse" />
                    ))}
                  </div>
                ))}
              </div>
              <div className="lg:hidden space-y-3">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="h-24 bg-zinc-800/30 rounded-2xl animate-pulse" />
                ))}
              </div>
            </div>
          ) : loadError ? (
            <div className="text-center py-20" data-testid="applications-error">
              <XCircle className="w-16 h-16 mx-auto mb-4 text-rose-400" />
              <p className="text-zinc-300 text-lg mb-2">{loadError}</p>
              <Button
                onClick={() => { setLoading(true); fetchApplications(); }}
                className="mt-4 bg-indigo-500 hover:bg-indigo-600 text-white rounded-full"
                data-testid="applications-retry-btn"
              >
                Retry
              </Button>
            </div>
          ) : applications.length === 0 ? (
            <div className="text-center py-20">
              <FolderOpen className="w-16 h-16 mx-auto mb-4 text-zinc-300" />
              <p className="text-zinc-500 text-lg mb-4">No applications yet</p>
              <Link to="/jobs">
                <Button className="bg-indigo-500 hover:bg-indigo-600 text-white rounded-full">
                  Browse Jobs
                </Button>
              </Link>
            </div>
          ) : (
            <>
              {/* Kanban Board - Desktop */}
              <div className="hidden lg:grid lg:grid-cols-5 gap-4">
                {Object.entries(groupedApplications).map(([status, apps]) => {
                  const page = getColumnPage(status);
                  const totalPages = Math.ceil(apps.length / ITEMS_PER_PAGE);
                  const pageApps = paginateItems(apps, page);

                  return (
                  <div key={status} className="min-h-[400px] flex flex-col">
                    <div className={`flex items-center gap-2 mb-4 px-3 py-2 rounded-lg ${statusConfig[status].color}`}>
                      {statusConfig[status].icon}
                      <span className="font-medium capitalize">{status}</span>
                      <span className="ml-auto bg-white/50 px-2 py-0.5 rounded-full text-xs">{apps.length}</span>
                    </div>
                    <div className="space-y-3 flex-1">
                      {pageApps.map((app) => (
                        <motion.div
                          key={app.id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="bg-white dark:bg-[#1c1c1c] rounded-xl border border-zinc-100 dark:border-white/[0.08] p-4 shadow-soft hover:shadow-hover transition-shadow"
                        >
                          <div className="flex items-start justify-between mb-2">
                            <h3
                              className={`font-medium text-zinc-900 dark:text-white text-sm line-clamp-1 ${app.job_url ? 'cursor-pointer hover:text-indigo-500 dark:hover:text-indigo-400 transition-colors' : ''}`}
                              onClick={() => app.job_url && window.open(app.job_url, '_blank')}
                              data-testid={`app-title-${app.id}`}
                            >
                              {app.job_title}
                            </h3>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button className="text-zinc-400 hover:text-zinc-600" data-testid={`app-menu-${app.id}`}>
                                  <MoreVertical className="w-4 h-4" />
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {Object.keys(statusConfig).map((s) => (
                                  <DropdownMenuItem key={s} onClick={() => updateStatus(app.id, s)}>
                                    Move to {statusConfig[s].label}
                                  </DropdownMenuItem>
                                ))}
                                <DropdownMenuItem 
                                  onClick={() => deleteApplication(app.id)}
                                  className="text-red-600"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                          <p className="text-xs text-zinc-500 mb-2">{app.company}</p>
                          <div className="flex items-center justify-between">
                            <span className={`px-2 py-0.5 rounded text-xs text-white ${sourceColors[app.source]}`}>
                              {app.source}
                            </span>
                            <span className="text-xs text-zinc-400">{formatDate(app.applied_at)}</span>
                          </div>
                          {PREP_STATUSES.includes(app.status) && (
                            <>
                              {app.job_url && (
                                <button
                                  onClick={() => window.open(app.job_url, '_blank')}
                                  className="mt-2 w-full flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg text-xs font-medium bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-500/20 transition-colors"
                                  data-testid={`complete-app-${app.id}`}
                                >
                                  <ExternalLink className="w-3 h-3" /> Apply on Company Site
                                </button>
                              )}
                              <button
                                onClick={() => updateStatus(app.id, 'applied')}
                                className="mt-2 w-full flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg text-xs font-medium bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-500/20 transition-colors"
                                data-testid={`mark-applied-${app.id}`}
                              >
                                <CheckCircle2 className="w-3 h-3" /> I applied — mark as Applied
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => contactRecruiter(app.id, app.company)}
                            disabled={contactingId === app.id || contactedIds.has(app.id)}
                            className={`mt-2 w-full flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                              contactedIds.has(app.id)
                                ? 'bg-emerald-50 text-emerald-600 cursor-default'
                                : 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100'
                            }`}
                            data-testid={`contact-recruiter-${app.id}`}
                          >
                            {contactingId === app.id ? (
                              <><Loader2 className="w-3 h-3 animate-spin" /> Sending...</>
                            ) : contactedIds.has(app.id) ? (
                              <><CheckCircle2 className="w-3 h-3" /> Pitch Sent</>
                            ) : (
                              <><Send className="w-3 h-3" /> Contact Recruiter</>
                            )}
                          </button>
                        </motion.div>
                      ))}
                    </div>
                    {totalPages > 1 && (
                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-zinc-100 dark:border-white/[0.06]" data-testid={`kanban-pagination-${status}`}>
                        <button
                          onClick={() => setColumnPage(status, page - 1)}
                          disabled={page === 0}
                          className="p-1 rounded-md text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                          data-testid={`kanban-prev-${status}`}
                        >
                          <ChevronLeft className="w-4 h-4" />
                        </button>
                        <span className="text-xs text-zinc-500 dark:text-zinc-400">{page + 1}/{totalPages}</span>
                        <button
                          onClick={() => setColumnPage(status, page + 1)}
                          disabled={page >= totalPages - 1}
                          className="p-1 rounded-md text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                          data-testid={`kanban-next-${status}`}
                        >
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>

              {/* List View - Mobile */}
              <div className="lg:hidden space-y-4">
                {paginatedListItems.map((app) => (
                  <motion.div
                    key={app.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white dark:bg-[#1c1c1c] rounded-2xl border border-zinc-100 dark:border-white/[0.08] shadow-soft overflow-hidden"
                  >
                    <div
                      className="p-5 cursor-pointer"
                      onClick={() => setExpandedApp(expandedApp === app.id ? null : app.id)}
                      data-testid={`app-expand-${app.id}`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1 min-w-0">
                          <h3
                            className={`font-heading font-semibold text-zinc-900 dark:text-white ${app.job_url ? 'hover:text-indigo-500 dark:hover:text-indigo-400' : ''}`}
                            onClick={(e) => { if (app.job_url) { e.stopPropagation(); window.open(app.job_url, '_blank'); } }}
                          >
                            {app.job_title}
                          </h3>
                          <p className="text-zinc-600 dark:text-zinc-400 text-sm">{app.company}</p>
                        </div>
                        <div className="flex items-center gap-2 ml-2 shrink-0">
                          {expandedApp === app.id ? <ChevronUp className="w-4 h-4 text-zinc-400" /> : <ChevronDown className="w-4 h-4 text-zinc-400" />}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button className="text-zinc-400 hover:text-zinc-600 p-1" onClick={(e) => e.stopPropagation()}>
                                <MoreVertical className="w-5 h-5" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {Object.keys(statusConfig).map((s) => (
                                <DropdownMenuItem key={s} onClick={() => updateStatus(app.id, s)}>
                                  Move to {statusConfig[s].label}
                                </DropdownMenuItem>
                              ))}
                              <DropdownMenuItem 
                                onClick={() => deleteApplication(app.id)}
                                className="text-red-600"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${statusConfig[app.status].color}`}>
                          {statusConfig[app.status].icon}
                          {statusConfig[app.status].label}
                        </span>
                        <span className={`px-3 py-1 rounded-full text-xs text-white ${sourceColors[app.source] || 'bg-zinc-500'}`}>
                          {app.source}
                        </span>
                        <span className="text-xs text-zinc-400 ml-auto">{formatDate(app.applied_at)}</span>
                      </div>
                    </div>

                    {/* Expandable Receipt Details */}
                    {expandedApp === app.id && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        transition={{ duration: 0.2 }}
                        className="border-t border-zinc-100 dark:border-white/[0.06]"
                      >
                        <div className="px-5 py-4 bg-zinc-50/50 dark:bg-white/[0.02] space-y-3" data-testid={`app-receipt-${app.id}`}>
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            <div>
                              <p className="text-xs text-zinc-400 dark:text-zinc-500 mb-0.5">Applied</p>
                              <p className="text-zinc-700 dark:text-zinc-300 text-xs font-medium">{formatDateTime(app.applied_at)}</p>
                            </div>
                            {app.cv_filename && (
                              <div>
                                <p className="text-xs text-zinc-400 dark:text-zinc-500 mb-0.5">CV Used</p>
                                <p className="text-zinc-700 dark:text-zinc-300 text-xs font-medium flex items-center gap-1">
                                  <FileText className="w-3 h-3 text-indigo-500" /> {app.cv_filename}
                                </p>
                              </div>
                            )}
                            {app.location && (
                              <div>
                                <p className="text-xs text-zinc-400 dark:text-zinc-500 mb-0.5">Location</p>
                                <p className="text-zinc-700 dark:text-zinc-300 text-xs font-medium flex items-center gap-1">
                                  <MapPin className="w-3 h-3 text-zinc-400" /> {app.location}
                                </p>
                              </div>
                            )}
                          </div>

                          {app.cover_letter && (
                            <div>
                              <p className="text-xs text-zinc-400 dark:text-zinc-500 mb-1">Cover Letter</p>
                              <p className="text-xs text-zinc-600 dark:text-zinc-400 leading-relaxed italic bg-white dark:bg-white/[0.04] rounded-lg p-3 border border-zinc-100 dark:border-white/[0.06] line-clamp-4">
                                {app.cover_letter}
                              </p>
                            </div>
                          )}

                          <div className="flex flex-col gap-2 pt-1">
                            {PREP_STATUSES.includes(app.status) && app.job_url && (
                              <Button
                                size="sm"
                                onClick={() => window.open(app.job_url, '_blank')}
                                className="w-full rounded-lg text-xs bg-amber-500 hover:bg-amber-600 text-white"
                                data-testid={`complete-app-mobile-${app.id}`}
                              >
                                <ExternalLink className="w-3 h-3 mr-1" /> Apply on Company Site
                              </Button>
                            )}
                            {PREP_STATUSES.includes(app.status) && (
                              <Button size="sm" variant="outline" onClick={() => updateStatus(app.id, 'applied')} className="w-full rounded-lg text-xs" data-testid={`mark-applied-mobile-${app.id}`}>
                                <CheckCircle2 className="w-3 h-3 mr-1" /> I applied — mark as Applied
                              </Button>
                            )}
                            <div className="flex gap-2">
                            {app.job_url && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => window.open(app.job_url, '_blank')}
                                className="flex-1 rounded-lg text-xs dark:border-white/[0.1] dark:text-zinc-300"
                                data-testid={`view-posting-${app.id}`}
                              >
                                <ExternalLink className="w-3 h-3 mr-1" /> View Posting
                              </Button>
                            )}
                            <button
                              onClick={() => contactRecruiter(app.id, app.company)}
                              disabled={contactingId === app.id || contactedIds.has(app.id)}
                              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                                contactedIds.has(app.id)
                                  ? 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 cursor-default'
                                  : 'bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-100 dark:hover:bg-indigo-500/20'
                              }`}
                              data-testid={`contact-recruiter-mobile-${app.id}`}
                            >
                              {contactingId === app.id ? (
                                <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Sending...</>
                              ) : contactedIds.has(app.id) ? (
                                <><CheckCircle2 className="w-3.5 h-3.5" /> Pitch Sent</>
                              ) : (
                                <><Send className="w-3.5 h-3.5" /> Contact Recruiter</>
                              )}
                            </button>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </motion.div>
                ))}
                {totalListPages > 1 && (
                  <div className="flex items-center justify-center gap-4 pt-4 pb-2" data-testid="list-pagination">
                    <button
                      onClick={() => setListPage(p => p - 1)}
                      disabled={listPage === 0}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium bg-white dark:bg-[#1c1c1c] border border-zinc-200 dark:border-white/[0.08] text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-white/[0.04] disabled:opacity-30 disabled:cursor-not-allowed transition-colors shadow-sm"
                      data-testid="list-prev-btn"
                    >
                      <ChevronLeft className="w-4 h-4" /> Prev
                    </button>
                    <span className="text-sm text-zinc-500 dark:text-zinc-400 font-medium">
                      {listPage + 1} of {totalListPages}
                    </span>
                    <button
                      onClick={() => setListPage(p => p + 1)}
                      disabled={listPage >= totalListPages - 1}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium bg-white dark:bg-[#1c1c1c] border border-zinc-200 dark:border-white/[0.08] text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-white/[0.04] disabled:opacity-30 disabled:cursor-not-allowed transition-colors shadow-sm"
                      data-testid="list-next-btn"
                    >
                      Next <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
      </div>
    </AppLayout>
  );
};

export default Applications;
